/**
 * PropertySubmitOfferPage
 *
 * Page for investors to submit an offer on a property.
 * Shows property summary on the left and offer form on the right.
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Redact, trackOfferSubmitted } from '@/common/analytics'
import { ApiError } from '@/common/api/client'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { Icon } from '@/common/components/icon'
import { Button } from '@/common/components/button'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { getProperty, type PropertyStatus } from '@/features/properties/api/properties'
import { STATUS_CONFIG, canInteractWithProperty } from '@/features/properties/components/PropertyStatusBadge'
import { PropertyKeyDetailsCard } from '@/features/properties/components/PropertyKeyDetailsCard'
import { createOffer, ClosingTimeline } from '@/features/offers/api/offers'
import {
  OfferFormFields,
  offerFormSchema,
  OFFER_FORM_DEFAULT_VALUES,
  type OfferFormData,
} from '@/features/offers/components/OfferForm'
import {
  DealViabilityInsight,
  DealViabilityMark,
} from '@/features/offers/components/DealViabilityInsight'
import { useOfferViability } from '@/features/offers/hooks/useOfferViability'
import { DEMO_PROPERTY_ID } from '@/features/product-tour'
import * as styles from './offer.css'

export const Route = createFileRoute({
  component: PropertySubmitOfferPage,
})

// =============================================================================
// Helpers
// =============================================================================

function formatSpecs(beds?: number | null, baths?: number | null, sqft?: number | null): string {
  const parts: string[] = []
  if (beds != null) parts.push(`${beds} beds`)
  if (baths != null) parts.push(`${baths} baths`)
  if (sqft != null) parts.push(`${sqft.toLocaleString()} sqft`)
  return parts.join('  ·  ')
}

// =============================================================================
// Component
// =============================================================================

type PageView = 'form' | 'confirmation'

export function PropertySubmitOfferPage() {
  const { id: propertyId } = useParams({ strict: false })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { token, roles } = useAuth()
  const { showErrorToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [view, setView] = useState<PageView>('form')

  const isInvestor = roles?.includes('investor')

  // Fetch property data
  const {
    data: property,
    isLoading: propertyLoading,
    error: propertyError,
  } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => getProperty(token!, propertyId!),
    enabled: !!token && !!propertyId,
  })

  // Redirect if property is sold/removed OR if user already has a pending/engaged/accepted offer
  useEffect(() => {
    if (!property) return

    // Can't submit offer on sold/removed properties
    if (!canInteractWithProperty(property.status as PropertyStatus)) {
      navigate({ to: '/properties/$id', params: { id: property.id } })
      return
    }

    // Can't submit another offer if one is pending, engaged or accepted
    if (
      property.offer_status === 'pending' ||
      property.offer_status === 'engaged' ||
      property.offer_status === 'accepted'
    ) {
      navigate({ to: '/properties/$id', params: { id: property.id } })
    }
  }, [property, navigate])

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OfferFormData>({
    resolver: zodResolver(offerFormSchema) as never,
    defaultValues: OFFER_FORM_DEFAULT_VALUES,
  })

  // How likely the amount currently in the field is to reach a conversation with the seller.
  // Debounced, and computed server-side — the floor price it keys on is not sent to the browser
  // for most properties.
  const offerPrice = watch('offer_price')
  const { insight, settledPrice } = useOfferViability(token, propertyId, offerPrice)

  const onSubmit = async (data: OfferFormData) => {
    if (!token || !propertyId) {
      showErrorToast('Please log in to submit an offer')
      return
    }

    if (!data.offer_price) {
      showErrorToast('Offer price is required')
      return
    }

    // Demo property from the product tour — skip the API call
    if (propertyId === DEMO_PROPERTY_ID) {
      setView('confirmation')
      return
    }

    setIsSubmitting(true)

    try {
      const offer = await createOffer(token, {
        property_id: propertyId,
        offer_price: data.offer_price,
        earnest_money_deposit: data.earnest_money_deposit ?? undefined,
        closing_timeline: data.closing_timeline as ClosingTimeline | undefined,
        notes: data.notes || undefined,
        private_notes: data.private_notes || undefined,
      })

      // Fire the Offer Submitted client event. Only the opaque
      // offer/property ids — never price/EMD/notes/address.
      trackOfferSubmitted({ offer_id: offer.id, property_id: propertyId })

      // Invalidate offers cache so the new offer shows up
      await queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })

      // Show confirmation view instead of navigating
      setView('confirmation')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit offer'
      showErrorToast(message)
      // An open offer already exists: refetch so the redirect above sends the investor to it
      if (err instanceof ApiError && err.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['property', propertyId] })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Non-investors shouldn't access this page
  if (!isInvestor) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text variant="bodyLg" weight="bold" color="error">
            Access Denied
          </Text>
          <Text color="muted">Only investors can submit offers.</Text>
          <Link to="/properties">
            <Button variant="primary">Back to Properties</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Loading state
  if (propertyLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (propertyError || !property) {
    return (
      <div className={styles.container}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back
        </Link>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="h4" className={styles.errorTitle}>Property Not Found</Text>
          <Text color="muted">
            The property you're looking for doesn't exist or you don't have permission to view it.
          </Text>
        </div>
      </div>
    )
  }

  // Get first image for property summary
  const firstImage = property.media?.find((m) => m.media_type === 'photo')

  // Confirmation view after successful submission
  if (view === 'confirmation') {
    return (
      <div className={styles.container}>
        {/* Back Link */}
        <Link to="/properties/$id" params={{ id: property.id }} className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back
        </Link>

        {/* Two-column layout same as form */}
        <div className={styles.content}>
          {/* Property Summary (left) */}
          <div className={styles.propertySummary}>
            {firstImage ? (
              <img
                src={firstImage.file_url}
                alt={property.address.street_address}
                className={styles.propertyImage}
              />
            ) : (
              <div className={styles.propertyImagePlaceholder}>
                <Icon name="home" size={48} className={styles.placeholderIcon} />
              </div>
            )}

            <div className={styles.propertyDetails}>
              <div className={styles.statusBadge} data-color={STATUS_CONFIG[property.status as PropertyStatus].color}>
                <span className={styles.statusDot} data-color={STATUS_CONFIG[property.status as PropertyStatus].color} />
                {STATUS_CONFIG[property.status as PropertyStatus].label}
              </div>

              <Text as="span" variant="body" weight="bold" className={styles.propertyAddress}>
                {property.address.street_address}
                {property.address.unit_number && ` ${property.address.unit_number}`}
              </Text>
              <Text as="span" variant="body" className={styles.propertyCityStateZip}>
                {property.address.city}, {property.address.state} {property.address.zipcode}
              </Text>
              <Text as="span" variant="body" color="muted" className={styles.propertySpecs}>
                {formatSpecs(property.beds, property.baths, property.sqft)}
              </Text>
            </div>
          </div>

          {/* Confirmation Card (right) */}
          <div className={styles.confirmationCard}>
            <Text variant="body" weight="bold" className={styles.confirmationTitle}>
              Offer submitted!
            </Text>
            <Text variant="body" className={styles.confirmationMessage}>
              You and the listing agent received an email confirmation with your offer details.
              Questions for the agent? You can respond with questions directly from your offer
              confirmation email or contact the agent directly.
            </Text>
            <Link to="/properties/$id" params={{ id: property.id }}>
              <Button variant="primary" className={styles.submitButton}>Back to property</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Back Link */}
      <Link to="/properties/$id" params={{ id: property.id }} className={styles.backLink}>
        <Icon name="arrow_back" size={16} />
        Back
      </Link>

      {/* Two-column layout */}
      <div className={styles.content}>
        {/* Property Summary (left) */}
        <div className={styles.propertySummary}>
          {firstImage ? (
            <img
              src={firstImage.file_url}
              alt={property.address.street_address}
              className={styles.propertyImage}
            />
          ) : (
            <div className={styles.propertyImagePlaceholder}>
              <Icon name="home" size={48} className={styles.placeholderIcon} />
            </div>
          )}

          <div className={styles.propertyDetails}>
            <div className={styles.statusBadge} data-color={STATUS_CONFIG[property.status as PropertyStatus].color}>
              <span className={styles.statusDot} data-color={STATUS_CONFIG[property.status as PropertyStatus].color} />
              {STATUS_CONFIG[property.status as PropertyStatus].label}
            </div>

            {/* The property address is PII — redact the display
                block (defense-in-depth on the primary offer surface). */}
            <Redact as="span" className={styles.propertyAddress}>
              <Text as="span" variant="body" weight="bold">
                {property.address.street_address}
                {property.address.unit_number && ` ${property.address.unit_number}`}
              </Text>
            </Redact>
            <Redact as="span" className={styles.propertyCityStateZip}>
              <Text as="span" variant="body">
                {property.address.city}, {property.address.state} {property.address.zipcode}
              </Text>
            </Redact>
            <Text as="span" variant="body" color="muted" className={styles.propertySpecs}>
              {formatSpecs(property.beds, property.baths, property.sqft)}
            </Text>
          </div>

          {/* Seller's terms and AVM, for reference while entering an offer */}
          <PropertyKeyDetailsCard property={property} />
        </div>

        {/* Offer Form (right) */}
        <div className={styles.formCard}>
          <Text variant="body" className={styles.formTitle}>Offer details</Text>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <OfferFormFields
              control={control}
              errors={errors}
              disabled={isSubmitting}
              priceMark={<DealViabilityMark insight={insight} />}
              afterPrice={
                settledPrice != null ? (
                  <DealViabilityInsight insight={insight} offerPrice={settledPrice} />
                ) : null
              }
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? 'Submitting...' : 'Submit offer'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
